import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import {
    CreateServiceTemplate,
    Environment,
    ENVIRONMENT,
    ServiceTemplateV2,
    UpdateServiceTemplate,
} from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT } from '@netfoundry-ui/shared/services';
import { PagedGetOption } from '@lagoshny/ngx-hateoas-client';
import { BaseHateoasResourceService } from './base-hateoas-resource.service';

/**
 * API client for persisted service templates
 * (core-management `/core/v3/service-templates`).
 *
 * Reads go through the HATEOAS base class (paged HAL collections). Writes use
 * raw HTTP so we send only the fields the `CreateServiceTemplate` /
 * `UpdateServiceTemplate` DTOs accept — the server owns `id`, `type`,
 * `createdAt`, and the soft-delete fields, and rejecting extra properties on
 * the write DTOs would otherwise cause round-trip failures.
 *
 * GLOBAL templates are read-only server-side: create/update/delete apply to
 * CUSTOM templates only. Callers must gate write actions in the UI.
 */
@Injectable({ providedIn: 'root' })
export class ServiceTemplateService extends BaseHateoasResourceService<ServiceTemplateV2> {
    private readonly endpoint = 'service-templates';

    constructor(
        @Inject(HTTP_CLIENT) protected override http: HttpClient,
        @Inject(ENVIRONMENT) protected override environment: Environment
    ) {
        super(http, environment, ServiceTemplateV2);
    }

    /** Fetch a single template by id. */
    getServiceTemplate(id: string): Promise<ServiceTemplateV2 | undefined> {
        return this.getResourceById(id);
    }

    /**
     * Fetch all templates visible to the caller. GLOBAL templates are always
     * returned; CUSTOM templates are scoped by the server to those the caller
     * may read. Pass a `networkGroupId` to additionally filter CUSTOM
     * templates to a single network group.
     */
    getServiceTemplates(networkGroupId?: string, options: PagedGetOption = {}): Promise<ServiceTemplateV2[]> {
        const params = networkGroupId ? { networkGroupId, ...(options.params ?? {}) } : options.params;
        return this.getResourcePage({ ...options, params });
    }

    /** Create a CUSTOM service template. */
    createServiceTemplate(model: CreateServiceTemplate): Observable<ServiceTemplateV2> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.post<ServiceTemplateV2>(this.baseUrl, model, { headers, responseType: 'json' });
    }

    /**
     * Update a CUSTOM service template. Uses PATCH so omitted fields are left
     * unchanged (the server accepts both PUT and PATCH on this handler).
     */
    updateServiceTemplate(id: string, model: UpdateServiceTemplate): Observable<ServiceTemplateV2> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.patch<ServiceTemplateV2>(`${this.baseUrl}/${id}`, model, { headers, responseType: 'json' });
    }

    /** Soft-delete a CUSTOM service template. Returns the soft-deleted entity; idempotent. */
    deleteServiceTemplate(id: string): Observable<ServiceTemplateV2> {
        return this.http.delete<ServiceTemplateV2>(`${this.baseUrl}/${id}`);
    }

    private get baseUrl(): string {
        return this.environment.v3apiUrl + this.endpoint;
    }
}
